import { z } from 'zod'
import { decimalSchema, positiveDecimalSchema, uuidSchema } from './common.ts'

/** Client-managed units; their code is snapshotted on every warehouse write. */
export const UNITS = ['kg', 'm', 'm2', 'pcs'] as const
/** Existing commercial catalog contract; warehouse codes are dynamic below. */
export const unitSchema = z.enum(UNITS)
export type Unit = z.infer<typeof unitSchema>
export const unitCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[a-zA-Z0-9._-]+$/)
export const warehouseUnitCodeSchema = unitCodeSchema
export const UNIT_LABELS: { [key: string]: string } = {
  kg: 'кг',
  m: 'м',
  m2: 'м²',
  pcs: 'шт',
  t: 'т',
  sheets: 'листів',
}
export const movementUnitSchema = warehouseUnitCodeSchema
export type MovementUnit = z.infer<typeof movementUnitSchema>

const unitMetadataSchema = z.object({
  name: z.string().trim().min(1).max(120),
  symbol: z.string().trim().min(1).max(32),
  precision: z.int().min(0).max(3),
})
export const unitInputSchema = unitMetadataSchema.extend({
  code: unitCodeSchema,
  precision: unitMetadataSchema.shape.precision.default(3),
})
export type UnitInput = z.infer<typeof unitInputSchema>
export const updateUnitInputSchema = unitMetadataSchema
  .partial()
  .refine((input) => Object.keys(input).length > 0)
export type UpdateUnitInput = z.infer<typeof updateUnitInputSchema>

export const managedUnitSchema = unitInputSchema.extend({
  id: uuidSchema,
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type ManagedUnit = z.infer<typeof managedUnitSchema>

export const decimalFitsNumeric = (value: string, precision: number, scale: number): boolean => {
  const unsigned = value.startsWith('-') ? value.slice(1) : value
  const [integer = '', rawFraction = ''] = unsigned.split('.')
  const fraction = rawFraction.replace(/0+$/, '')
  const integerDigits = integer.replace(/^0+/, '').length || 1
  return fraction.length <= scale && integerDigits <= precision - scale
}

/** Entered quantity × factor = a material's canonical base quantity. */
export const materialUnitFactorInputSchema = z.object({
  unitCode: warehouseUnitCodeSchema,
  factorToBase: positiveDecimalSchema.refine(
    (value) => decimalFitsNumeric(value, 24, 9),
    'Коефіцієнт має відповідати формату numeric(24,9)',
  ),
})
export type MaterialUnitFactorInput = z.infer<typeof materialUnitFactorInputSchema>

export const materialUnitFactorSchema = materialUnitFactorInputSchema.extend({
  id: uuidSchema,
  materialId: uuidSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type MaterialUnitFactor = z.infer<typeof materialUnitFactorSchema>

export const unitConversionSnapshotSchema = z.object({
  enteredUnit: warehouseUnitCodeSchema,
  enteredQuantity: decimalSchema,
  normalizedQuantity: decimalSchema,
  conversionFactor: positiveDecimalSchema,
})
export type UnitConversionSnapshot = z.infer<typeof unitConversionSnapshotSchema>
